import React, { useEffect, useRef, useState } from "react";
import { Plus } from "lucide-react";
import { api } from "../../lib/apiClient";

/**
 * CustomerAutocomplete — prefix-match search after 3 chars.
 * Lets any user add a new customer when no exact match exists.
 * Anti-scrape: suggestion list is non-selectable, copy/contextmenu blocked.
 */
export function CustomerAutocomplete({ value, onChange, onPickCustomer }) {
  const [open, setOpen] = useState(false);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const wrapRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Debounced prefix search after 3 chars
  useEffect(() => {
    const q = (value || "").trim();
    if (q.length < 3) {
      setMatches([]);
      setOpen(false);
      return;
    }
    setLoading(true);
    const t = setTimeout(() => {
      api
        .get(`/customers/search?q=${encodeURIComponent(q)}`)
        .then((r) => setMatches(r.data || []))
        .catch(() => setMatches([]))
        .finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(t);
  }, [value]);

  const exactMatch = matches.some((m) => m.name.toLowerCase() === (value || "").trim().toLowerCase());
  const canCreate = (value || "").trim().length >= 3 && !exactMatch;

  const select = (customer) => {
    onChange(customer.name);
    if (onPickCustomer) onPickCustomer(customer);
    setOpen(false);
  };

  const createNew = async () => {
    const name = (value || "").trim();
    if (!name) return;
    setCreating(true);
    setError("");
    try {
      const { data } = await api.post("/customers", { name });
      onChange(data.name);
      if (onPickCustomer) onPickCustomer(data);
      setOpen(false);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not add customer");
    } finally {
      setCreating(false);
    }
  };

  // Block copy/cut/contextmenu/dragstart on the dropdown list
  const blockCopy = (e) => { e.preventDefault(); e.stopPropagation(); };

  return (
    <div className="relative" ref={wrapRef}>
      <input
        value={value || ""}
        onChange={(e) => { onChange(e.target.value); setOpen(true); setError(""); }}
        onFocus={() => { if ((value || "").trim().length >= 3) setOpen(true); }}
        className="qb-input"
        data-testid="customer-name-input"
        placeholder="Customer or company name — 3 character minimum"
        autoComplete="off"
      />
      {open && (
        <div
          className="absolute z-50 mt-1 w-full max-h-64 overflow-y-auto bg-white border border-slate-300 shadow-lg"
          data-testid="customer-suggestions"
          onCopy={blockCopy}
          onCut={blockCopy}
          onContextMenu={blockCopy}
          onDragStart={blockCopy}
          style={{ userSelect: "none", WebkitUserSelect: "none", MozUserSelect: "none" }}
        >
          {loading && (
            <div className="px-3 py-2 text-xs text-slate-500">Searching…</div>
          )}
          {!loading && matches.length === 0 && (value || "").trim().length >= 3 && (
            <div className="px-3 py-2 text-xs text-slate-500">No customers match "{value}".</div>
          )}
          {!loading && matches.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => select(m)}
              className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 focus:bg-blue-50 outline-none flex items-center justify-between gap-3"
              data-testid={`customer-suggestion-${m.id}`}
            >
              <span className="truncate">{m.name}</span>
              {(m.surcharge_amount || 0) > 0 && (
                <span className="shrink-0 text-[10px] uppercase tracking-wider font-semibold text-brand-orange bg-orange-50 border border-orange-200 rounded px-1.5 py-0.5">
                  +£{Number(m.surcharge_amount).toFixed(2)} surcharge
                </span>
              )}
            </button>
          ))}
          {canCreate && (
            <div className="border-t border-slate-200">
              <button
                type="button"
                onClick={createNew}
                disabled={creating}
                className="w-full text-left px-3 py-2 text-sm bg-slate-50 hover:bg-brand-blue hover:text-white text-brand-blue font-semibold inline-flex items-center gap-2 disabled:opacity-60"
                data-testid="customer-create-button"
                style={{ userSelect: "none" }}
              >
                <Plus size={14} /> {creating ? "Adding…" : `Add "${(value || "").trim()}" as a new customer`}
              </button>
            </div>
          )}
          {error && <div className="px-3 py-1 text-xs text-red-600">{error}</div>}
        </div>
      )}
    </div>
  );
}
